import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as ToastrActions from '../../core/store/toastr.actions';

import { of } from 'rxjs';
import { currentOrganization } from '../../core/auth';
import { AppState } from '../../core/core.state';
import { KbService } from '../services/kb.service';
import * as KbActions from './kb.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { Update } from '@ngrx/entity';
import { KbArticle } from '../models/kbarticle.model';
import { ActivatedRoute, Router } from '@angular/router';
import { I18nService } from '../../core/i18n';


@Injectable()
export class KbEffects {
    private i18n = inject(I18nService);

    kbRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbRequest),
        mergeMap(action => combineLatest([
                of(action),
                this.store.pipe( select(currentOrganization), filter(o => o != null), take(1) )
                ])
        ),
        mergeMap( ([action, org])  => this.KbService.getKb(org.id)),
        switchMap( res => [
            KbActions.KbRequestSuccess({ Kb: res.model})
        ])
    
    ));

    kbArticleRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbArticleRequest),
        mergeMap( action  => this.KbService.getKbArticle(action.article_id).pipe(
            switchMap( res => {
                const updated_article: Update<KbArticle> = {
                    id: action.article_id,
                    changes: {
                        article: res.model.article
                    }
                };
                return [
                    KbActions.KbArticleRequestSuccess({ article: updated_article})
                ];
            }),
            catchError((er: HttpErrorResponse) => of(
                ToastrActions.showToastrError({message: this.i18n.t('kb.problems_while_retrieving_article_the', 'Problems while retrieving article. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    
    ));

    kbArticleSaveRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbArticleSaveRequest),
        mergeMap(action => this.KbService.saveKbArticle(action.article).pipe(
            switchMap(res => [
                KbActions.KbArticleSaveSuccess({article: res.model}),
                ToastrActions.showToastrSuccess({message: this.i18n.t('kb.article_was_successfully_saved', 'Article was successfully saved.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                KbActions.KbArticleSaveFailed({message: 'Problems while saving the article' }),
                ToastrActions.showToastrError({message: this.i18n.t('kb.problems_while_saving_article_the_error', 'Problems while saving article. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    kbArticleDeleteRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbArticleDeleteRequest),
        mergeMap(action => this.KbService.deleteArticle(action.article_id).pipe(
            switchMap(res => [
                KbActions.KbArticleDeleteSuccess({article_id: action.article_id}),
                ToastrActions.showToastrSuccess({message: this.i18n.t('kb.article_was_successfully_deleted', 'Article was successfully deleted.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                KbActions.KbArticleDeleteFailed({message: 'Problems while deleting the article' }),
                ToastrActions.showToastrError({message: this.i18n.t('kb.problems_while_deleting_the_article_the', 'Problems while deleting the article. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    kbCategorySaveRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbCategorySaveRequest),
        mergeMap(action => this.KbService.saveKbCategory(action.category).pipe(
            switchMap(res => [
                KbActions.KbCategorySaveSuccess({category: res.model}),
                ToastrActions.showToastrSuccess({message: this.i18n.t('kb.article_was_successfully_saved', 'Article was successfully saved.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                KbActions.KbCategorySaveFailed({message: 'Problems while saving the category' }),
                ToastrActions.showToastrError({message: this.i18n.t('kb.problems_while_saving_category_the_error', 'Problems while saving category. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));
    
    kbCategoryDeleteRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbCategoryDeleteRequest),
        mergeMap(action => this.KbService.deleteCategory(action.category_id).pipe(
            switchMap(res => [
                KbActions.KbCategoryDeleteSuccess({category_id: action.category_id}),
                ToastrActions.showToastrSuccess({message: this.i18n.t('kb.category_was_successfully_deleted', 'Category was successfully deleted.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                KbActions.KbCategoryDeleteFailed({message: 'Problems while deleting the category' }),
                ToastrActions.showToastrError({message: this.i18n.t('kb.problems_while_deleting_the_category_the', 'Problems while deleting the category. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    kbCategoriesOrderRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbCategoryOrderRequest),
        mergeMap( action => this.KbService.saveCategoriesOrder(action.ids, action.property_id).pipe(
            switchMap(res => [
                KbActions.KbCategoryOrderSuccess({categories: res.model}),
                ToastrActions.showToastrSuccess({message: this.i18n.t('kb.categories_were_successfully_rearranged', 'Categories were successfully rearranged.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                KbActions.KbCategoryOrderFailed({message: 'Problems while rearranging categories.' }),
                ToastrActions.showToastrError({message: this.i18n.t('kb.problems_while_rearranging_categories', 'Problems while rearranging categories.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    kbArticlesOrderRequest = createEffect(() => this.actions$.pipe(
        ofType(KbActions.KbArticleOrderRequest),
        mergeMap( action => this.KbService.saveArticlesOrder(action.ids, action.property_id).pipe(
            switchMap(res => [
                KbActions.KbArticleOrderSuccess({articles: res.model}),
                ToastrActions.showToastrSuccess({message: this.i18n.t('kb.articles_were_successfully_rearranged', 'Articles were successfully rearranged.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                KbActions.KbArticleOrderFailed({message: 'Problems while rearranging articles.' }),
                ToastrActions.showToastrError({message: this.i18n.t('kb.problems_while_rearranging_articles', 'Problems while rearranging articles.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    ngrxOnInitEffects(): Action {
        return { type: KbActions.KbRequest.type };
    }

    constructor(
        private store: Store<AppState>,
        private actions$: Actions,
        private KbService: KbService,
        private router: Router,
        private activatedRoute: ActivatedRoute
    ) { }

}